import type { ApolloClient, NormalizedCacheObject } from "@apollo/client"
import { MutationResult, MutationTuple } from "@apollo/client"
import {
  ServerBlockTypeType,
  getBlockTypeKey,
  getStyles
} from "@pathwright/blocks-core"
import { FirebaseProvider } from "@pathwright/utils"
import { captureException } from "@sentry/browser"
import debounce from "lodash/debounce"
import PropTypes from "prop-types"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"
import { getBlockType } from "../block/utils"
import { useBlocksConfig } from "../config/BlocksConfigProvider"
import { BLOCKS_MODE, BLOCKS_RELOAD_DOCUMENT_EVENT } from "../config/constants"
import * as editorActionTypes from "../editor/constants"
import BlocksApolloProvider from "../graphql/BlocksApolloProvider"
import {
  useBlocksContentOnly,
  useBlocksContext,
  useBlocksMutations,
  useDiscardDraft,
  usePublish
} from "../graphql/hooks"
import { ReducerActionType } from "../syncer/useSyncedReducer"
import {
  BLOCKS_MODE_TYPE,
  BlockType,
  BlocksContentType,
  BlocksContextType
} from "../types"
import { getPasteFailureMessage } from "../utils/pasteBlockUtils"
import { scopeContentData, scopeUserData } from "../utils/scope"
import { createID } from "../utils/utils"
import * as viewerActionTypes from "../viewer/constants"
import { DefaultError } from "./DefaultError"
import { DefaultLoading } from "./DefaultLoading"
import {
  ContentNoticeRibbon,
  MigrationBlockingOverlay,
  MigrationLoadingView,
  MigrationStagingError,
  isStagingEnvironmentUnavailableError,
  useMigrationSyncerState,
  useRouteTokenVariables
} from "./MigrationStatus"
import SyncerContext from "./SyncerContext"
import { BatchBlockType, BlockBatchType, initBatch } from "./batch"

// While user-initiated changes are synced via the syncToGraphQL function,
// sometimes props like mode or userID can change without user interaction,
// and require notifying consumers of the GraphQLSyncer
// so that they can reset their content state accordingly.
// We must wait until the new server content has loaded before signaling a reset.
const useNeedsContentReset = (
  serverContent: BlocksContentType | null,
  mode: BLOCKS_MODE_TYPE,
  userID: number,
  isLoading: boolean
): boolean => {
  const [needsContentReset, setNeedsContentReset] = useState<boolean>(false)

  // Keep track of the previous mode and userID
  const prevModeRef = useRef<BLOCKS_MODE_TYPE | null>(null)
  const prevUserIDRef = useRef<number | null>(null)

  useEffect(() => {
    const modeChanged = prevModeRef.current !== mode
    const userChanged = prevUserIDRef.current !== userID

    if (!isLoading && serverContent && (modeChanged || userChanged)) {
      setNeedsContentReset(true)

      // Immediately reset the signal to prevent redundant resets
      setTimeout(() => setNeedsContentReset(false), 0)

      // Update the refs only after signaling a reset
      prevModeRef.current = mode
      prevUserIDRef.current = userID
    }
  }, [serverContent, mode, userID, isLoading])

  return needsContentReset
}

type GraphQLSyncerPropsType = {
  children: JSX.Element | JSX.Element[]
  contextKey: string
  offline?: boolean
  template?: string
  userID?: string | number
  mode?: BLOCKS_MODE_TYPE
  initialBlocks?: {
    blocksContext: BlocksContextType
    content: BlocksContentType
  }
  onDataLoaded?: ({
    content,
    blocksContext
  }: {
    content: BlocksContentType
    blocksContext: BlocksContextType
  }) => void
  onSave?: ({ content }: { content: BlocksContentType }) => void
  changeSaveInterval?: number
  onReload?: () => void
  renderLoading?: (message?: string) => ReactNode
  renderError?: (error: any, onReload?: any) => JSX.Element
  isPreviewMode?: boolean
}

// Handles syncing of blocks to the GQL server. Expects GraphQLBlocksContextProvider
// above it to have already fetched base BlocksContext, so blockTypes/context metadata
// is available from context.
const GraphQLSyncer = ({
  children,
  ...props
}: GraphQLSyncerPropsType): JSX.Element | ReactNode => {
  const blocksContext: BlocksContextType = useGraphQLBlocksContext()
  const blockTypes: ServerBlockTypeType[] = blocksContext.blockTypes

  const [hasReportedContent, setHasReportedContent] = useState(false)

  const saveBatchRef = useRef<BlockBatchType>({})
  const inFlightSaveCountRef = useRef(0)
  const latestEditorBlockVersionRef = useRef<Record<string, number>>({})
  // backgroundSave resolves after syncToGraphQL returns, so it needs a stable
  // way to dispatch START/STOP_SYNCING from that later async boundary.
  const syncDispatchRef = useRef<
    ((action: ReducerActionType) => void) | null
  >(null)

  const {
    contextKey,
    offline,
    onSave,
    template,
    userID,
    mode,
    initialBlocks,
    onDataLoaded,
    changeSaveInterval,
    onReload,
    renderLoading = () => null,
    renderError = (error: any, onReload?: () => void) => null,
    isPreviewMode
  } = props

  const contentRouteToken = blocksContext?.contentRouteToken || undefined
  const { routeTokenVariables, disableRouteToken } = useRouteTokenVariables({
    contextKey,
    contentRouteToken
  })

  const {
    loading: contentLoading,
    error: blocksGQLError,
    data,
    refetch: refetchContent
  } = useBlocksContentOnly({
    variables: {
      mode,
      contextKey,
      ...routeTokenVariables,
      template,
      userID,
      upsert: mode === BLOCKS_MODE.EDIT,
      draft: mode === BLOCKS_MODE.EDIT
    },
    skip: offline === true || !!initialBlocks?.content,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all"
  })

  const serverContent = initialBlocks?.content || data?.content || null

  // Set initial blocks content immediately for smoother UX.
  const [content, setContent]: [BlocksContentType | null, any] =
    useState<BlocksContentType | null>(serverContent)

  const contentID = serverContent?.id ? `${serverContent.id}` : ""
  const { migrationIsPending, migrationNeedsReload, clearMigrationReload } =
    useMigrationSyncerState({
      contextKey,
      blocksContext,
      contentID: contentID || undefined,
      serverContentID: serverContent?.id,
      contentLoading: !!contentLoading,
      hasContent: !!content,
      disableRouteToken
    })

  // In-app reload: refetch content (no full page reload). GraphQLSyncer owns Apollo so it can refetch.
  // When refetchContent isn't available (e.g. query skipped), fall back to full page reload so the user can recover.
  const handleReloadDocument = useCallback(() => {
    if (onReload) {
      onReload()
      return
    }
    clearMigrationReload()
    disableRouteToken()
    if (refetchContent) {
      refetchContent()
    } else if (typeof window !== "undefined") {
      window.location.reload()
    }
  }, [clearMigrationReload, disableRouteToken, onReload, refetchContent])

  // When migration permissions change (e.g. admin enables Blocks rollout), refresh this step if mounted.
  useEffect(() => {
    if (typeof window === "undefined") return
    const handler = () => handleReloadDocument()
    window.addEventListener(BLOCKS_RELOAD_DOCUMENT_EVENT, handler)
    return () =>
      window.removeEventListener(BLOCKS_RELOAD_DOCUMENT_EVENT, handler)
  }, [handleReloadDocument])

  const needsContentReset = useNeedsContentReset(
    serverContent,
    mode as BLOCKS_MODE_TYPE,
    userID as number,
    contentLoading || false
  )

  useEffect(() => {
    // Fire an event with all blocks data here for anybody up on high that might want to do stuff
    // with it
    if (!onDataLoaded) return

    if (data && !contentLoading && !blocksGQLError) {
      onDataLoaded({ content: data.content, blocksContext })
    }
  }, [contentLoading])

  useEffect(() => {
    // Don't set state content from server content if we have pending batch items
    if (saveBatchRef.current && Object.keys(saveBatchRef.current).length) return

    if (!serverContent) return

    setContent(serverContent)
    // This 2nd dependency check below ensures that when the save batch has been emptied,
    // there will be a final state content update from the server content
  }, [serverContent, Object.keys(saveBatchRef.current || {}).length])

  const [discardDraft]: MutationTuple<any, any> = useDiscardDraft({
    variables: {
      contentID: contentID || undefined,
      contextKey,
      ...routeTokenVariables
    },
    onCompleted: () => {
      if (onSave) onSave({ content: serverContent })
    }
  })

  const [publish]: MutationTuple<any, any> = usePublish({
    variables: {
      id: contentID || undefined,
      contextKey,
      ...routeTokenVariables
    },
    onCompleted: () => {
      if (onSave) onSave({ content: serverContent })
    }
  })

  const blocksMutations: Record<any, MutationTuple<any, any> | any> =
    useBlocksMutations({
      contentID,
      blockTypes,
      onCompleted: () => {
        if (onSave) onSave({ content: serverContent })
      }
    })

  const [moveBlock]: MutationTuple<any, any> = blocksMutations.moveBlock
  const [deleteBlock]: MutationTuple<any, any> = blocksMutations.deleteBlock
  const [pasteBlock]: MutationTuple<any, any> = blocksMutations.pasteBlock
  const [pasteBlocks]: MutationTuple<any, any> = blocksMutations.pasteBlocks

  useEffect(() => {
    const batch: BlockBatchType = initBatch()
    saveBatchRef.current = batch
  }, [])

  // This handles a scenario where the Blocks content hasn't been properly "selected" by the parent client
  // Could happen if parent Step has been pasted and no change has been made to Blocks
  useEffect(() => {
    if (content && mode === BLOCKS_MODE.EDIT && onSave && !hasReportedContent) {
      onSave({ content })
      setHasReportedContent(true)
    }
  }, [content])

  const mergeBatchBlock = (block: BlockType): BlockType => {
    const { id } = block
    const batchBlock = saveBatchRef.current[id]

    if (!batchBlock) return block

    const mergedBlock: BlockType = { ...block }

    // Only merge fields that are part of BlockType so internal batch metadata
    // like callback/clientVersion never leaks into reducer state.
    if (batchBlock.type !== undefined) mergedBlock.type = batchBlock.type
    if (batchBlock.layout !== undefined) mergedBlock.layout = batchBlock.layout
    if (batchBlock.order !== undefined) mergedBlock.order = batchBlock.order
    if (batchBlock.data !== undefined) mergedBlock.data = batchBlock.data
    if (batchBlock.style !== undefined) mergedBlock.style = batchBlock.style

    return mergedBlock
  }

  const getNextEditorBlockVersion = useCallback((blockId: string): number => {
    const nextVersion = (latestEditorBlockVersionRef.current[blockId] || 0) + 1
    latestEditorBlockVersionRef.current[blockId] = nextVersion
    return nextVersion
  }, [])

  const shouldApplyEditorBlockResult = useCallback(
    (blockId: string, clientVersion?: number): boolean => {
      if (clientVersion == null) return true

      if (latestEditorBlockVersionRef.current[blockId] !== clientVersion) {
        return false
      }

      const queuedBlock = saveBatchRef.current[blockId]
      return !queuedBlock || queuedBlock.clientVersion === clientVersion
    },
    []
  )

  const hasPendingSaves = useCallback(
    (): boolean =>
      // Previous behavior only reflected reducer actions and individual
      // mutation completions; this also counts debounced-yet-unsent work and
      // requests currently in flight.
      !!Object.keys(saveBatchRef.current || {}).length ||
      inFlightSaveCountRef.current > 0,
    []
  )

  // Receives the latest state and reducer action, after the reducer has run the action locally.
  // Like a reducer but doesn't return a new state
  // Syncs to the server and then updates local state.
  const syncToGraphQL = async (
    action: ReducerActionType,
    state: Record<string, any>,
    dispatch: (action: ReducerActionType) => void
  ) => {
    syncDispatchRef.current = dispatch
    const { type, payload }: ReducerActionType = action

    if (isPreviewMode) {
      return state
    }

    dispatch({ type: editorActionTypes.START_SYNCING })

    switch (type) {
      case editorActionTypes.ADD_BLOCK_REQUEST:
        try {
          const result = await handleAddBlock({
            ...payload
          })

          if (!result || !result.data || !result.data.block) {
            dispatch({ type: editorActionTypes.ADD_BLOCK_ERROR })
            break
          }

          dispatch({
            type: editorActionTypes.SYNCED_ADD_BLOCK,
            payload: result.data.block
          })
        } catch (error) {
          console.error(error)
          dispatch({ type: editorActionTypes.ADD_BLOCK_ERROR })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.PASTE_BLOCK:
        try {
          const { copiedBlockClip } = state
          const { index } = payload

          // Nothing to see here
          if (!copiedBlockClip) {
            dispatch({
              type: editorActionTypes.PASTE_BLOCK_ERROR,
              payload: { error: "Nothing copied to paste." }
            })
            break
          }

          const [sourceContentID, sourceBlockID] =
            copiedBlockClip.object_id.split(":")

          const order = index + 1

          const result = await handlePasteBlock({
            sourceContentID,
            sourceBlockID,
            order
          })
          const copiedBlock = result?.data?.pasteBlock
          if (!copiedBlock) {
            throw new Error("The copied block is no longer available.")
          }

          dispatch({
            type: editorActionTypes.SYNCED_PASTE_BLOCK,
            payload: { copiedBlock }
          })
        } catch (error) {
          const pasteError = getPasteFailureMessage(error)
          console.error("Paste block error:", error, pasteError)
          dispatch({
            type: editorActionTypes.PASTE_BLOCK_ERROR,
            payload: { error: pasteError }
          })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.PASTE_BLOCKS:
        try {
          const { copiedBlockClip } = state
          const { index, copies } = payload

          // Nothing to see here
          if (!copiedBlockClip) {
            dispatch({
              type: editorActionTypes.PASTE_BLOCK_ERROR,
              payload: { error: "Nothing copied to paste." }
            })
            break
          }

          const [sourceContentID, sourceBlockID] =
            copiedBlockClip.object_id.split(":")

          const result = await handlePasteBlocks({
            sourceContentID,
            sourceBlockID,
            startingOrder: index + 1,
            copies
          })
          const copiedBlocks = result?.data?.pasteBlocks
          if (!Array.isArray(copiedBlocks) || !copiedBlocks.length) {
            throw new Error("The copied block is no longer available.")
          }

          dispatch({
            type: editorActionTypes.SYNCED_PASTE_BLOCKS,
            payload: { copiedBlocks }
          })
        } catch (error) {
          const pasteError = getPasteFailureMessage(error)
          console.error("Paste blocks error:", error, pasteError)
          dispatch({
            type: editorActionTypes.PASTE_BLOCK_ERROR,
            payload: { error: pasteError }
          })
        }
        break
      case editorActionTypes.UPDATE_BLOCK_DATA:
        try {
          const block = state.blocks.find((b: BlockType) => b.id === payload.id)
          const clientVersion = getNextEditorBlockVersion(payload.id)

          handleUpdateBlock(
            {
              ...block,
              data: {
                ...block.data,
                ...payload.block.data
              },
              clientVersion,
              style: getStyles(block)
            },
            (result: MutationResult) => {
              if (
                !result?.data?.block ||
                !shouldApplyEditorBlockResult(payload.id, clientVersion)
              ) {
                return
              }

              dispatch({
                type: editorActionTypes.SYNCED_UPDATE_BLOCK,
                payload: mergeBatchBlock(result.data.block)
              })
            }
          )
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.UPDATE_BLOCK_LAYOUT:
        try {
          const block = state.blocks.find((b: BlockType) => b.id === payload.id)
          const clientVersion = getNextEditorBlockVersion(payload.id)

          handleUpdateBlock(
            {
              type: block.type,
              id: block.id,
              data: block.data,
              clientVersion,
              style: getStyles(block),
              layout: payload.layout,
              order: block.order
            },
            (result: MutationResult) => {
              if (
                !result?.data?.block ||
                !shouldApplyEditorBlockResult(payload.id, clientVersion)
              ) {
                return
              }

              dispatch({
                type: editorActionTypes.SYNCED_UPDATE_BLOCK,
                payload: mergeBatchBlock(result.data.block)
              })
            }
          )
        } catch (error) {
          console.error(error)
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.UPDATE_BLOCK_STYLE:
        try {
          const block = state.blocks.find((b) => b.id === payload.id)
          const clientVersion = getNextEditorBlockVersion(payload.id)

          delete block.style._typename

          handleUpdateBlock(
            {
              type: block.type,
              id: block.id,
              data: block.data,
              clientVersion,
              style: block.style,
              layout: block.layout,
              order: block.order
            },
            (result: MutationResult) => {
              if (
                !result?.data?.block ||
                !shouldApplyEditorBlockResult(payload.id, clientVersion)
              ) {
                return
              }

              dispatch({
                type: editorActionTypes.SYNCED_UPDATE_BLOCK,
                payload: mergeBatchBlock(result.data.block)
              })
            }
          )
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.MOVE_BLOCK_UP:
      case editorActionTypes.MOVE_BLOCK_DOWN:
        try {
          const block = state.blocks.find((b) => b.id === payload.id)
          if (!block) return

          const result = await handleMoveBlock({
            id: block.id,
            order: block.order
          })
          dispatch({
            type: editorActionTypes.SYNCED_BLOCKS,
            payload: result.data.content
          })
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.DELETE_BLOCK:
        // This means there's a pending delete
        if (state.deleteBlockID) return

        // Make sure the block exists in the server content
        const block = content!.blocks.find((b) => b.id === payload.id)
        if (!block) return

        try {
          const result = await handleDeleteBlock({ id: payload.id })

          dispatch({
            type: editorActionTypes.SYNCED_BLOCKS,
            payload: result.data.content
          })
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case editorActionTypes.START_PUBLISH:
        try {
          // Flush any debounced background saves before publishing.
          await backgroundSave.flush()
          const result = await publish()
          dispatch({
            type: editorActionTypes.SYNCED_PUBLISH,
            payload: result.data.content
          })
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
        }
        break
      case editorActionTypes.START_DISCARD_DRAFT:
        try {
          const result = await discardDraft()
          dispatch({
            type: editorActionTypes.SYNCED_DISCARD_DRAFT,
            payload: result.data.content
          })
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      case viewerActionTypes.UPDATE_USER_BLOCK:
        try {
          const block = state.blocks.find((b) => b.id === payload.id)

          if (!block) {
            console.error(
              "Can't save user block — no block found for id: ",
              payload.id
            )
            return
          }

          handleUpdateUserBlock(
            {
              ...block,
              data: {
                ...block.data,
                ...payload.data
              },
              userID: payload.userID,
              draft: payload.draft,
              trackProgress: payload.trackProgress
            },
            (result: MutationResult) => {
              dispatch({
                type: viewerActionTypes.SYNCED_USER_BLOCK,
                payload: mergeBatchBlock(result.data.block)
              })
            }
          )
        } catch (error) {
          dispatch({ type: editorActionTypes.ERROR, payload: error })
          // could also re-dispatch the action for a "retry" on network error
          // dispatch(action)
        }
        break
      default:
        // GQL doesn't have to handle all local actions
        // console.log("Unknown action type in GQL Syncer: ", action)
        dispatch({ type: editorActionTypes.STOP_SYNCING })
        return state
    }
  }

  // const checkForErrors = result => {
  //   if (!result || result?.data) {
  //     return true
  //   }

  //   if (result.graphQLErrors) {
  //     const errorMessage = result.graphQLErrors.length
  //       ? result.graphQLErrors[0].message
  //       : "Oops, it looks like we ran into a problem!"

  //     return true
  //   }

  //   return false
  // }

  const handleMoveBlock = async ({ id, order }) => {
    if (saveBatchRef.current[id]) {
      saveBatchRef.current[id]!.order = order
    }

    return await moveBlock({
      variables: {
        contentID,
        contextKey,
        ...routeTokenVariables,
        id,
        order
      }
    })
  }

  const handleDeleteBlock = async ({ id }) => {
    // remove block from saveBatch
    delete saveBatchRef.current[id]

    return await deleteBlock({
      variables: {
        contentID,
        contextKey,
        ...routeTokenVariables,
        id
      }
    })
  }

  const handlePasteBlock = async ({
    sourceContentID,
    sourceBlockID,
    order
  }) => {
    return await pasteBlock({
      variables: {
        contextKey,
        ...routeTokenVariables,
        sourceContentID,
        sourceBlockID,
        destinationContentID: content!.id,
        order
      }
    })
  }

  const handlePasteBlocks = async ({
    sourceContentID,
    sourceBlockID,
    startingOrder,
    copies
  }) => {
    return await pasteBlocks({
      variables: {
        contextKey,
        ...routeTokenVariables,
        sourceContentID,
        sourceBlockID,
        destinationContentID: content!.id,
        startingOrder,
        copies
      }
    })
  }

  const handleAddBlock = async ({
    id,
    type,
    data,
    layout,
    order = undefined,
    style = undefined
  }) => {
    // This ID is a temporary placeholder until we get the real one from the API
    const ensuredId = id || `new-${createID()}`
    const newBlock = {
      id: ensuredId,
      type,
      data,
      layout,
      order,
      style,
      draft: true
    }

    const result = await _handleSaveBlock({
      ...newBlock
    })
    return result
  }

  const _handleSaveBlock = async ({
    type = "",
    id,
    data = {},
    layout,
    order = undefined,
    style = undefined,
    draft,
    userID = "",
    trackProgress = false,
    scope = undefined
  }: BatchBlockType): Promise<any> => {
    const isUser = scope === "user"
    data = isUser
      ? scopeUserData(data, getBlockType(type, blockTypes))
      : scopeContentData(data, getBlockType(type, blockTypes))

    const typeKey = getBlockTypeKey(type)
    const mutation = isUser
      ? `save${typeKey}BlockUserData`
      : `save${typeKey}Block`

    let variables: {
      contentID: string
      contextKey: string
      id: string
      data: any
      layout?: string
      order?: number
      style?: Record<string, any>
      userID?: string
      draft?: boolean
      noProgress?: boolean
      routeToken?: string
    } = {
      contentID: content!.id,
      contextKey,
      ...routeTokenVariables,
      id,
      data
    }

    if (isUser) {
      variables.noProgress = !trackProgress
      variables.draft = draft
    }

    if (layout) variables.layout = layout
    // Send numeric order only; one-based index
    if (order) variables.order = Number(order)
    if (style) variables.style = style
    if (userID) variables.userID = userID

    const [mutate] = blocksMutations[mutation]

    if (offline) {
      console.log("offline mutation attempted: ", mutation, variables)
      return
    }

    // MUTATION
    const result: MutationResult = await mutate({
      variables
    }).catch((e) => {
      console.error(e)
    })

    return result
  }

  // Apply block updates that are waiting in the saveBatch
  const backgroundSave = useCallback(
    debounce(
      async () => {
        // Accumulate the list of bg save promises.
        const bgSavePromises = Object.keys(saveBatchRef.current).reduce(
          (acc, key) => {
            const batchBlock = saveBatchRef.current[key]
            delete saveBatchRef.current[key]
            // Block could have been removed in a previous sync
            if (batchBlock) {
              inFlightSaveCountRef.current += 1
              acc.push(
                _handleSaveBlock({ ...batchBlock, callback: null }).then(
                  (result) => {
                    inFlightSaveCountRef.current = Math.max(
                      0,
                      inFlightSaveCountRef.current - 1
                    )

                    if (batchBlock.callback && result?.data) {
                      try {
                        batchBlock.callback(result)
                      } catch (error) {
                        captureException(error)
                      }
                    }

                    const syncDispatch = syncDispatchRef.current
                    if (syncDispatch) {
                      // If another save is still queued or in flight, keep
                      // syncing active instead of letting this completion
                      // make the UI look done too early.
                      if (hasPendingSaves()) {
                        syncDispatch({
                          type: editorActionTypes.START_SYNCING
                        })
                      } else if (!result?.data) {
                        syncDispatch({
                          type: editorActionTypes.STOP_SYNCING
                        })
                      }
                    }

                    return result
                  }
                )
              )
            }
            return acc
          },
          [] as Promise<any>[]
        )

        // Finally, wait for all bg save promises to complete.
        return Promise.all(bgSavePromises)
      },
      changeSaveInterval,
      { leading: true }
    ),
    [content?.blocks, saveBatchRef.current]
  )

  const handleUpdateBlock = async (
    { type, id, data, layout, order = null, style, clientVersion },
    callback: ((result: MutationResult) => void) | null = null
  ) => {
    const batchBlock: BatchBlockType = {
      type,
      id,
      data,
      layout,
      clientVersion,
      draft: true
    }

    if (order) batchBlock.order = order
    if (style) batchBlock.style = style

    const key = id
    saveBatchRef.current[key] = batchBlock
    saveBatchRef.current[key].callback = callback

    backgroundSave()
  }

  const handleUpdateUserBlock = (
    { type, id, data, userID, layout, trackProgress = false, draft = true },
    callback: ((result: MutationResult) => void) | null = null
  ) => {
    const batchBlock = {
      type,
      id,
      data,
      layout,
      userID,
      trackProgress,
      draft
    }

    const key = id
    saveBatchRef.current[key] = { ...batchBlock, scope: "user" }
    saveBatchRef.current[key].callback = callback

    backgroundSave()
  }

  if (blocksGQLError) {
    if (isStagingEnvironmentUnavailableError(blocksGQLError)) {
      return (
        <MigrationStagingError
          error={blocksGQLError}
          onReload={handleReloadDocument}
        />
      )
    }
    const message = blocksGQLError.message || String(blocksGQLError)
    return (
      renderError?.(blocksGQLError, handleReloadDocument) ?? (
        <DefaultError message={message} />
      )
    )
  }

  // When migration completed elsewhere we have content + overlay; don't show loading even if
  // contentLoading is true (disableRouteToken triggers a refetch).
  const showLoading =
    (contentLoading || !content || migrationIsPending) &&
    !(migrationNeedsReload && content)
  if (showLoading) {
    if (migrationIsPending) return <MigrationLoadingView />
    return renderLoading?.() ?? <DefaultLoading />
  }

  return (
    <SyncerContext.Provider
      // Migration changes document and block IDs together.
      // Remount consumers when we adopt that document so their reducers cannot retain old IDs.
      key={content.id}
      value={{
        content,
        needsContentReset,
        contentLoading: !!contentLoading,
        blocksContext,
        blockTypes,
        syncer: syncToGraphQL
      }}
    >
      <div style={{ position: "relative" }} className="GraphQLSyncer--wrapper">
        <ContentNoticeRibbon
          notice={serverContent?.contentNotice ?? undefined}
        />
        {children}
        <MigrationBlockingOverlay
          migrationNeedsReload={migrationNeedsReload}
          onReload={handleReloadDocument}
        />
      </div>
    </SyncerContext.Provider>
  )
}

GraphQLSyncer.propTypes = {
  contextKey: PropTypes.string.isRequired,
  offline: PropTypes.bool,
  onSave: PropTypes.func,
  template: PropTypes.string,
  userID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mode: PropTypes.string,
  initialBlocks: PropTypes.shape({
    blocksContext: PropTypes.object,
    content: PropTypes.object
  }),
  onDataLoaded: PropTypes.func,
  changeSaveInterval: PropTypes.number,
  onReload: PropTypes.func,
  renderLoading: PropTypes.func,
  renderError: PropTypes.func
}

const GraphQLBlocksContext = createContext<BlocksContextType>({
  accountID: "3",
  mediaStoragePath: "",
  userID: "1",
  blockTypes: []
})

const useGraphQLBlocksContext = (): BlocksContextType =>
  useContext(GraphQLBlocksContext)

// Fetches BlocksContext for static context data plus migration routing metadata.
const GraphQLBlocksContextProvider = ({
  children,
  renderLoading = () => null,
  renderError = (error: any, onReload?: () => void) => (
    <DefaultError message={error.message || String(error)} />
  ),
  ...props
}: {
  children: ReactNode
  renderLoading?: (message?: string) => ReactNode
  renderError?: (error: any, onReload?: () => void) => ReactNode
  onReload?: () => void
  initialBlocks?: {
    blocksContext: BlocksContextType
    content: BlocksContentType
  }
  contextKey: string
}) => {
  const { onReload, initialBlocks, contextKey } = props

  const {
    loading,
    error,
    data = {}
  } = useBlocksContext({
    variables: { contextKey },
    skip: !!initialBlocks?.blocksContext,
    // Routing tokens are specific to this load. A second consumer (such as the
    // step card's publish panel) must not replace an open editor's token through
    // the shared cache and trigger a content refetch/remount.
    fetchPolicy: "no-cache"
  })

  const blocksContext = initialBlocks?.blocksContext || data?.blocksContext

  if (loading) return renderLoading?.() ?? <DefaultLoading />

  if (error) {
    const message = error.message || String(error)
    return (
      renderError?.(message, onReload) ?? <DefaultError message={message} />
    )
  }

  if (!blocksContext || !blocksContext.blockTypes) {
    const message = "404: can't find Blocks"
    return (
      renderError?.(message, onReload) ?? <DefaultError message={message} />
    )
  }

  return (
    <GraphQLBlocksContext.Provider value={blocksContext}>
      {children}
    </GraphQLBlocksContext.Provider>
  )
}

const GraphQLSyncerContainer = ({
  children,
  graphQLEndpoint,
  graphQLClient,
  authToken,
  accountID
}: {
  children: JSX.Element
  graphQLEndpoint?: string
  graphQLClient?: ApolloClient<NormalizedCacheObject>
  authToken?: string | null
  accountID?: string
}): JSX.Element => {
  const {
    contextKey,
    offline,
    onSave,
    template = undefined,
    userID = undefined,
    mode,
    initialBlocks,
    onDataLoaded,
    changeSaveInterval,
    onReload,
    renderLoading,
    isPreviewMode,
    firebaseConfig
  } = useBlocksConfig()
  const normalizedFirebaseConfig = firebaseConfig
    ? {
        url: firebaseConfig.url ?? undefined,
        client_api_key: firebaseConfig.client_api_key ?? undefined
      }
    : undefined
  const normalizedFirebaseAuthToken =
    firebaseConfig?.firebaseAuthToken ?? undefined

  const providerAndSyncer = (
    <FirebaseProvider
      firebaseConfig={normalizedFirebaseConfig}
      firebaseAuthToken={normalizedFirebaseAuthToken}
    >
      <GraphQLBlocksContextProvider
        contextKey={contextKey}
        onReload={onReload}
        initialBlocks={initialBlocks}
        renderLoading={renderLoading}
      >
        <GraphQLSyncer
          contextKey={contextKey}
          offline={offline}
          onSave={onSave}
          template={template}
          userID={userID}
          mode={mode}
          onDataLoaded={onDataLoaded}
          changeSaveInterval={changeSaveInterval}
          onReload={onReload}
          initialBlocks={initialBlocks}
          renderLoading={renderLoading}
          isPreviewMode={isPreviewMode}
        >
          {children}
        </GraphQLSyncer>
      </GraphQLBlocksContextProvider>
    </FirebaseProvider>
  )

  // A host may supply its Apollo client through context instead of props.
  if (!graphQLClient && !graphQLEndpoint) {
    return providerAndSyncer
  }

  return (
    <BlocksApolloProvider
      client={graphQLClient}
      endpoint={graphQLEndpoint}
      authToken={authToken}
    >
      {providerAndSyncer}
    </BlocksApolloProvider>
  )
}

GraphQLSyncerContainer.propTypes = {
  children: PropTypes.node,
  graphQLEndpoint: PropTypes.string,
  accountID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export { GraphQLBlocksContextProvider, GraphQLSyncer, useGraphQLBlocksContext }
export default GraphQLSyncerContainer
